import type { User } from '@supabase/supabase-js';
import { AbstractViewModel, Logger } from '@footsteps/shared';
import type { WozCaseManagerState, WozCaseJSON } from './woz-case-manager.state';
import { WozCaseEntity } from './woz-case/entities';
import { FlowStepId } from './woz-case/woz-chatbot-flows';
import { WozCaseStorageService } from './services/storage/woz-case-storage.service';
import { WozCaseStorageDatabaseAdapter } from './services/storage/woz-case-storage-database.adapter';

export class WozCaseManagerViewModel extends AbstractViewModel<WozCaseManagerState> {
  static initialState: WozCaseManagerState = {
    auth: {
      isResolved: false,
      hasUser: false,
    },
    captcha: {
      isReady: false,
      isVerifying: false,
    },
    cases: {
      isLoading: false,
      error: null,
      openCases: [],
    },
    modals: {
      showResumePicker: false,
      showActiveCasePrompt: false,
    },
    currentCase: null,
    isCreatingCase: false,
    isStartDisabled: true,
    canResume: false,
    isCaseReady: false,
    isFlowComplete: false,
  };

  private readonly logger: Logger;
  user: User | null = null;

  private pendingExecuteCaptcha: (() => void) | null = null;

  private signInAnonymously: ((captchaToken?: string) => Promise<void>) | null = null;

  private readonly local = new WozCaseStorageService('local');

  private readonly database = new WozCaseStorageDatabaseAdapter();

  constructor() {
    super(WozCaseManagerViewModel.initialState);
    this.logger = new Logger(this.constructor.name);

    this.setCaptchaReady = this.setCaptchaReady.bind(this);
    this.onCaptchaVerified = this.onCaptchaVerified.bind(this);
    this.onCaptchaError = this.onCaptchaError.bind(this);
    this.resumeActiveCase = this.resumeActiveCase.bind(this);
    this.requestResume = this.requestResume.bind(this);
    this.confirmNewCase = this.confirmNewCase.bind(this);
    this.dismissActiveCasePrompt = this.dismissActiveCasePrompt.bind(this);
    this.dismissResumePicker = this.dismissResumePicker.bind(this);
  }

  setSignInAnonymously(fn: (captchaToken?: string) => Promise<void>) {
    this.signInAnonymously = fn;
  }

  setUser(user: User | null) {
    const prevId = this.user?.id ?? null;
    const nextId = user?.id ?? null;

    if (this.state.getValue().auth.isResolved && prevId === nextId) {
      return;
    }

    const wasLoggedIn = this.user !== null;
    this.user = user;
    this.update((state) => {
      state.auth.isResolved = true;
      state.auth.hasUser = user !== null;
      state.captcha.isReady = false;
      state.captcha.isVerifying = false;
    });

    if (wasLoggedIn && user === null) {
      this.clearCurrentCase();
      this.clearSessionStorage();
    }

    this.refreshStartDisabled();
    this.loadOpenCases();
  }

  private get isKnownUser(): boolean {
    return !!this.user && !this.user.is_anonymous;
  }

  async loadOpenCases(): Promise<void> {
    this.setCasesLoading();

    try {
      await this.local.init();
      const localEntity = await this.local.load();
      let dbEntities: WozCaseEntity[] = [];

      if (this.isKnownUser) {
        dbEntities = await this.database.fetchOpen();
      }

      // localStorage always has the freshest state, so when the same
      // case exists in both sources (same ID), prefer the local version.
      const localId = localEntity?.id ?? null;

      const items: WozCaseJSON[] = dbEntities
        .filter((entity) => entity.id !== localId)
        .map((entity) => entity.toJSON());

      if (localEntity) {
        items.push(localEntity.toJSON());
      }

      this.update((state) => {
        state.cases.openCases = items;
        state.cases.isLoading = false;
      });

      if (!this.state.getValue().currentCase && localEntity) {
        this.setCurrentCase(localEntity.toJSON());
      }

      this.refreshCanResume();
      this.refreshStartDisabled();
    } catch (error) {
      this.setCasesError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  /**
   * Called when the user presses "Start".
   * If logged in and there are open cases, shows the active-case prompt modal.
   * Otherwise proceeds with case creation (or captcha if anonymous).
   */
  startCase(executeCaptcha: () => void) {
    const state = this.state.getValue();

    if (state.auth.hasUser && state.cases.openCases.length > 0) {
      this.pendingExecuteCaptcha = executeCaptcha;
      this.setShowActiveCasePrompt(true);
      return;
    }

    this.beginCaseCreation(executeCaptcha);
  }

  resumeActiveCase() {
    this.setShowActiveCasePrompt(false);
    this.pendingExecuteCaptcha = null;

    const { cases } = this.state.getValue();
    if (cases.openCases.length === 1) {
      this.selectCase(cases.openCases[0]);
    } else if (cases.openCases.length > 1) {
      this.setShowResumePicker(true);
    }
  }

  async confirmNewCase(): Promise<void> {
    this.setShowActiveCasePrompt(false);
    this.clearCurrentCase();
    this.clearSessionStorage();

    const captchaFn = this.pendingExecuteCaptcha;
    this.pendingExecuteCaptcha = null;

    if (captchaFn) {
      this.beginCaseCreation(captchaFn);
    }
  }

  async selectCase(wozCase: WozCaseJSON): Promise<void> {
    this.setShowResumePicker(false);

    if (wozCase.source === 'database' && wozCase.id) {
      const fullEntity = await this.database.load(wozCase.id);
      if (fullEntity) {
        await this.local.save(fullEntity);
        this.setCurrentCase(fullEntity.toJSON());
        this.setIsCaseReady(true);
        return;
      }
    }

    this.setCurrentCase(wozCase);
    this.setIsCaseReady(true);
  }

  setCaptchaReady() {
    this.update((state) => {
      state.captcha.isReady = true;
    });
    this.refreshStartDisabled();
  }

  onCaptchaVerified(token: string) {
    this.setCaptchaVerifying(false);
    this.createCase(token);
  }

  onCaptchaError() {
    this.setCaptchaVerifying(false);
  }

  dismissActiveCasePrompt() {
    this.setShowActiveCasePrompt(false);
    this.pendingExecuteCaptcha = null;
  }

  /**
   * Called when the user presses the "Resume" button.
   * If there's exactly one open case, resume it directly.
   * If there are multiple, show the resume picker modal.
   */
  requestResume(): void {
    const { cases, currentCase } = this.state.getValue();

    if (currentCase && cases.openCases.length === 0) {
      this.setIsCaseReady(true);
      return;
    }

    if (cases.openCases.length === 1) {
      this.selectCase(cases.openCases[0]);
      return;
    }

    if (cases.openCases.length > 1) {
      this.setShowResumePicker(true);
    }
  }

  dismissResumePicker() {
    this.setShowResumePicker(false);
  }

  setIsCaseReady(isCaseReady: boolean) {
    this.update((state) => {
      state.isCaseReady = isCaseReady;
    });
  }

  /** Set the current active case and persist to storage. */
  private setCurrentCase(wozCase: WozCaseJSON) {
    const entity = new WozCaseEntity(wozCase);
    const json = entity.toJSON();

    this.update((state) => {
      state.currentCase = json;
      state.isFlowComplete = json.flow?.completedSteps?.some((s) => s.id === FlowStepId.FlowComplete) ?? false;
    });

    if (entity.isLocal) {
      this.local.save(entity);
    }

    this.refreshCanResume();
    this.refreshStartDisabled();
  }

  /**
   * Clear all session data from localStorage.
   * With the unified storage, clearing the case also clears messages and flow.
   */
  private clearSessionStorage() {
    this.local.clear();
  }

  /** Called after a case is successfully submitted — clears the active case and refreshes the list. */
  onCaseSubmitted() {
    this.update((state) => {
      state.currentCase = null;
      state.isFlowComplete = false;
    });
    this.local.clear();
    this.loadOpenCases();
  }

  /** Clear the current case and remove from storage. */
  private clearCurrentCase() {
    const current = this.state.getValue().currentCase;

    this.update((state) => {
      state.currentCase = null;
      state.isFlowComplete = false;
    });

    if (current?.source === 'local') {
      this.local.clear();
    }

    this.refreshCanResume();
    this.refreshStartDisabled();
  }

  private setCaptchaVerifying(value: boolean) {
    this.update((state) => {
      state.captcha.isVerifying = value;
    });
    this.refreshStartDisabled();
  }

  private setShowActiveCasePrompt(value: boolean) {
    this.update((state) => {
      state.modals.showActiveCasePrompt = value;
    });
  }

  private setShowResumePicker(value: boolean) {
    this.update((state) => {
      state.modals.showResumePicker = value;
    });
  }

  private setCreatingCase(value: boolean) {
    this.update((state) => {
      state.isCreatingCase = value;
    });
    this.refreshStartDisabled();
  }

  private setCasesLoading() {
    this.update((state) => {
      state.cases.isLoading = true;
      state.cases.error = null;
      state.cases.openCases = [];
    });
    this.refreshStartDisabled();
  }

  private setCasesError(error: Error) {
    this.update((state) => {
      state.cases.isLoading = false;
      state.cases.error = error;
      state.cases.openCases = [];
    });
    this.refreshStartDisabled();
    this.refreshCanResume();
  }

  private beginCaseCreation(executeCaptcha: () => void) {
    if (this.user) {
      this.createCase();
    } else {
      this.setCaptchaVerifying(true);
      executeCaptcha();
    }
  }

  private async createCase(captchaToken?: string): Promise<void> {
    this.setCreatingCase(true);

    try {
      if (!this.user && this.signInAnonymously) {
        await this.signInAnonymously(captchaToken);
      }

      const source = this.user ? 'database' : 'local';
      const entity = new WozCaseEntity({ source, status: 'open' });
      this.setCurrentCase(entity.toJSON());

      this.setCreatingCase(false);
      this.setIsCaseReady(true);
    } catch (error) {
      this.logger.error('Failed to create case', error);
      this.setCreatingCase(false);
    }
  }

  private refreshStartDisabled() {
    const state = this.state.getValue();
    const disabled =
      !state.auth.isResolved ||
      state.isCreatingCase ||
      state.cases.isLoading ||
      (!state.auth.hasUser && (!state.captcha.isReady || state.captcha.isVerifying));

    if (state.isStartDisabled !== disabled) {
      this.update((state) => {
        state.isStartDisabled = disabled;
      });
    }
  }

  private refreshCanResume() {
    const { auth, cases, currentCase } = this.state.getValue();
    const canResume = auth.hasUser && !cases.isLoading && (currentCase !== null || cases.openCases.length > 0);

    if (this.state.getValue().canResume !== canResume) {
      this.update((state) => {
        state.canResume = canResume;
      });
    }
  }
}
